var FIGURE = FIGURE || {};
// @vimeo/player ships an ES module as its `module` entry, which webpack prefers over the UMD
// `main`, so a bare require() hands back the { default } namespace rather than the constructor.
// Same unwrap as crossfade-carousel.js — see the note there for what the missing `new` broke.
var VimeoPlayer = require('@vimeo/player');
VimeoPlayer = VimeoPlayer.default || VimeoPlayer;

FIGURE.videoHero = function () {
    // How long a visitor browsing the list on their own keeps control before playback takes
    // the list back. Every interaction restarts it, so this is idle time, not a deadline.
    var RESYNC_DELAY = 6000;
    // How long the "Play Sound" label beside the mute button stays up once playback starts, if it
    // is neither taken nor dismissed.
    var SOUND_LABEL_DELAY = 5000;
    // Quiet time after the last scroll event before the list is treated as having come to rest on an
    // item. Only static mode still scrolls, so this only applies there. Long enough to sit out
    // trackpad momentum, short enough not to feel deferred.
    var SCROLL_SETTLE_DELAY = 120;
    // How long to wait on a silent Vimeo embed before showing the list without it.
    var READY_TIMEOUT = 8000;
    // How far the local clock may run past the newest position Vimeo reported before it stops
    // extrapolating. The embed reports every ~266ms, so this is two missed reports: long enough to
    // ride out an irregular one, short enough that a stall or a throttled tab cannot let the list
    // run away from the picture. The list holds instead, and the next report brings it back.
    var CLOCK_LEAD = 0.5;
    // Time going backwards by more than this is the video looping rather than a correction to the
    // estimate. Anything smaller is absorbed by the monotonic guard in estimatedSeconds().
    var WRAP_BACK = 1;
    // Fraction of the remaining distance closed per frame while the list is easing back onto the
    // drift after an interruption. Converges to under a pixel in about twenty frames.
    var CATCHUP_RATE = 0.16;
    // Seconds the last item's fade out and back in takes, and the same for a clicked jump. Matches
    // SHIFT_MS in crossfade-carousel.js.
    var WRAP_FADE = 0.8;

    // The age gate — switched on per site in the CMS, rendered by the core _modals/age-gate, and
    // shown by OffsetBaseResources on window load, which is after this file has run — is a
    // static-backdrop modal, so nothing of the hero is visible or reachable while it is up. The
    // video used to spend that time playing to no one: the run-in over before the gate was
    // dismissed, the sound prompt already counted down and faded behind the backdrop, and the
    // audio the only part of it that ever got through. Holding the start also leaves the embed
    // unloaded until it is wanted, rather than merely muted behind the gate.
    //
    // The lookup has to wait for document-ready even though everything else here runs inline:
    // _layouts/index.tpl.php renders _snippets/javascript before _snippets/modal, so at the point
    // this file executes the gate is further down the document and not parsed yet. Searching for
    // it then always comes back empty, which reads as "no gate" and starts the video — exactly
    // the behaviour this is here to prevent. Core's own age gate code sidesteps the same ordering
    // by doing all of its work on window load.
    function whenAgeGateCleared(start) {
        $(document).ready(function () {
            var gate = document.getElementById('offsetAgeGate');
            // No gate on the page (disabled, or a logged-in visitor), or one already answered on
            // an earlier visit — the same cookie OBR reads before deciding to show it. Either way
            // it never opens, so there is no event coming and waiting would strand the hero on
            // its poster with the list still hidden.
            if (!gate || $.cookie('ageGateCookie') === 'oldEnough') {
                start();
                return;
            }
            // Bootstrap fires its modal events through jQuery, which calls jQuery handlers
            // directly rather than dispatching a real DOM event — a native addEventListener for
            // 'hidden.bs.modal' would never hear this one. Attached before OBR shows the gate on
            // window load, so there is no window in which the dismissal could be missed.
            $(gate).one('hidden.bs.modal', start);
        });
    }

    document.querySelectorAll('[data-hero]').forEach(function (hero) {
        var nav = hero.querySelector('[data-hero-nav]');
        var track = hero.querySelector('[data-hero-track]');
        var iframe = hero.querySelector('[data-hero-video]');
        var controls = hero.querySelector('[data-hero-controls]');
        var playBtn = hero.querySelector('[data-hero-play]');
        var muteBtn = hero.querySelector('[data-hero-mute]');
        var dimBtn = hero.querySelector('[data-hero-dim]');
        var liveRegion = hero.querySelector('[data-hero-live]');

        if (!nav || !track) return;

        var items = nav.querySelectorAll('[data-hero-item]');
        var itemCount = items.length;
        if (!itemCount) return;

        // The list is linear. The video loops, but the last item hands back to the first by fading
        // the text out, jumping and fading in, rather than travelling on. See wrapEased().
        var slots = Array.prototype.slice.call(items);

        var player = null;
        // Starts at 0 because that is what the template rendered active. Anything else and the
        // first setActive() would mistake the server's markup for its own state and skip the
        // class work — leaving item 0 lit through the run-in.
        var current = 0;
        var liveIndex = -1;
        var detached = false;
        var idleTimer;
        var isMuted = true;
        // The "Play Sound" label is a one-time invitation; this is what keeps a resumed playback
        // from bringing it back. See showSoundLabel().
        var soundLabelShown = false;

        // The video's runtime, and the list's position within it.
        //
        // Timing is derived rather than authored: every item owns an equal slice of the duration, so
        // the list needs no per-item timestamps and the CMS `sort` is the timeline. That is also why
        // the duration has to arrive before any of this can be placed — see onTimeUpdate.
        var duration = 0;
        // Scroll offset that centres each item, cached because the drift loop must not read layout:
        // measuring offsetTop after writing scrollTop on the same frame forces a reflow, sixty times
        // a second. Refilled by applyGeometry(), which is the only thing that moves them.
        var offsets = [];
        // The newest position Vimeo reported, and the wall clock when it arrived. Frames between
        // reports are extrapolated from the pair; see estimatedSeconds().
        var clockSeconds = 0;
        var clockAt = 0;
        var clockRunning = false;
        var lastEstimate = 0;
        // Whether the drift owns the scroll position. Not the same as the loop running: a paused
        // video keeps ownership so the list holds where it was frozen rather than being handed
        // mid-handover to mandatory snap, which would jump it to the nearer item.
        var owned = false;
        var rafId = null;
        var catchUp = false;
        var onScreen = true;
        var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        // Last value written per key, so a frame that changes nothing touches no styles. Holds both
        // the items' --hero-t and the CTAs' opacity; the keys are prefixed apart.
        var paintedValues = {};

        /**
         * Nav geometry — window height, where the list is scrolled to, compact mode.
         *
         * The window is a little over three items tall: one item plus a slack's worth of room at each
         * end, and the slack is now most of an item rather than a sliver of one. Playback drifts the
         * list through it continuously, so for much of the time two items are on screen mid-travel —
         * one leaving, one arriving — with the third at the far edge inside the fade. See the note
         * above --hero-slack in _video-hero.scss for why the room was widened.
         *
         * The window is a real scroll container rather than a transform, and it stays one even though
         * nothing but code scrolls it any more: `overflow: hidden` blocks a gesture without blocking
         * an assignment to scrollTop, so the drift keeps its mechanism and the visitor gets no grip on
         * it. The one exception is .is-static, where the video never started and scrolling is the only
         * way the words past the first can be reached at all.
         *
         * The track is padded by a slack at both ends so the first and last items can reach the
         * centre — a scroll container cannot be pushed past its own content the way a transform
         * could.
         */

        // Geometry the stylesheet owns, read rather than duplicated here — which is why those values
        // are written in px there: a custom property comes back as specified, so anything in rem
        // would need resolving by hand. See the note above --hero-slack for how they relate.
        function navMetric(name) {
            return parseFloat(window.getComputedStyle(nav).getPropertyValue(name)) || 0;
        }

        // Remeasured rather than taken once, because the item heights this is built from move with
        // the viewport's *width* as the copy rewraps — so it can be wrong after a resize that never
        // changed the mode, and it is also read before webfonts have necessarily settled.
        //
        // Every item is given the height of the tallest, and the window is sized from that. Titles
        // wrap to anywhere between one and three lines, so left to their natural heights the items
        // differ by enough that what shows either side of the one in front swings with whichever item
        // that is — measured at 56px against 86px on this content. Uniform boxes make the effect
        // identical wherever the drift has got to, which is the whole point of an effect that repeats.
        // They are also what makes the pitch constant, which the drift's own arithmetic depends on.
        //
        // The alternative, a window sized to the item in front, keeps that uniform too but changes the
        // window's height as the list moves, taking the hero's bottom-left corner and both fade edges
        // with it.
        //
        // The padding is what lets the first and last items reach the centre of the window. With
        // uniform items it is the slack itself, at both ends.
        //
        // Two things have to be undone before measuring, or the answer is read back off the last
        // pass rather than off the content. The heights below, because an item already carrying the
        // previous tallest can only ever make the next tallest the same or larger — the window would
        // never shrink after a resize. And compact mode, which hides every item but the one in
        // front, so measuring through it would read a single item and conclude the window fits
        // anywhere. Nothing paints in between; this is all one task.
        function measureGeometry() {
            var wasCompact = nav.classList.contains('is-compact');
            if (wasCompact) nav.classList.remove('is-compact');
            clearItemHeights();

            var tallest = 0;
            for (var i = 0; i < itemCount; i++) {
                tallest = Math.max(tallest, items[i].offsetHeight);
            }

            var slack = navMetric('--hero-slack');
            if (wasCompact) nav.classList.add('is-compact');

            return {
                itemHeight: tallest,
                // A slack's worth of room at each end, on top of the item itself. That is what keeps
                // the mask clear of the active item's own first and last lines, and it is the only
                // place the items either side are ever seen.
                height: tallest + 2 * slack,
                pad: slack
            };
        }

        function clearItemHeights() {
            for (var i = 0; i < slots.length; i++) {
                slots[i].style.height = '';
            }
        }

        function applyGeometry() {
            var geometry = measureGeometry();
            for (var i = 0; i < slots.length; i++) {
                slots[i].style.height = geometry.itemHeight + 'px';
            }
            nav.style.height = geometry.height + 'px';
            track.style.paddingTop = geometry.pad + 'px';
            track.style.paddingBottom = geometry.pad + 'px';
            cacheOffsets();
        }

        // Read here and nowhere else. The drift writes scrollTop every frame, so a layout read from
        // inside the loop would be a forced reflow on each one; these move only when the geometry
        // does, which is this function and compact mode.
        function cacheOffsets() {
            offsets = [];
            for (var i = 0; i < slots.length; i++) {
                offsets.push(Math.round(
                    slots[i].offsetTop - (nav.clientHeight - slots[i].offsetHeight) / 2
                ));
            }
        }

        applyGeometry();

        // Where the list has to be scrolled to for `index` to be the item in front.
        function offsetFor(index) {
            var item = items[index];
            if (!item) return 0;
            return Math.round(item.offsetTop - (nav.clientHeight - item.offsetHeight) / 2);
        }

        function setPosition(index, animate) {
            // Compact mode shows the item in front on its own, so there is nothing to scroll to.
            if (nav.classList.contains('is-compact')) return;
            // The drift is writing scrollTop every frame from the video's clock. Anything else
            // moving the list at the same time is two authors on one value: the assignment below
            // would be overwritten within 16ms, and while it stood it would put the list somewhere
            // the clock disagrees with. setActive() still runs for the class and ARIA work — it just
            // does not get to place the list while playback is doing that.
            if (owned) return;

            var target = offsetFor(index);
            if (Math.abs(nav.scrollTop - target) < 1) return;

            if (animate) {
                // Assigned rather than scrollTo'd so the stylesheet keeps control of whether this
                // glides or cuts — scroll-behavior is honoured here, and it is what the
                // reduced-motion block switches off along with everything else.
                nav.scrollTop = target;
                return;
            }

            nav.scrollTo({ top: target, behavior: 'instant' });
        }

        setPosition(current, false);

        /**
         * Which item is in front.
         *
         * index === -1 is the run-in: on a fresh load the player is ready but has not reported a
         * position yet, so there is no runtime to divide and nowhere to place the list. It is pulled
         * back rather than showing an item the video may not be on. The track is parked underneath, so
         * coming out of it is a fade rather than a scroll in from nowhere. Only ever entered once, and
         * only for the quarter-second or so between ready() and the first timeupdate — from there the
         * list always has a position, the loop included.
         */
        function setActive(index, animate, announce) {
            // Clearing the run-in cannot hang off the index changing. The template renders the
            // list hidden with item 0 already marked active, so the very first call — the first
            // reported position, or a fallback settling on the first item — is the one case where the
            // item in front is not changing and yet the list still has to be revealed.
            if (index >= 0) nav.classList.remove('is-run-in');

            if (index === current) return;

            if (current >= 0) {
                items[current].classList.remove('active');
                items[current].querySelector('[data-hero-select]').setAttribute('aria-pressed', 'false');
                var leavingCta = items[current].querySelector('[data-hero-cta]');
                if (leavingCta) leavingCta.setAttribute('tabindex', '-1');
            }

            if (index < 0) {
                current = -1;
                nav.classList.add('is-run-in');
                setPosition(0, false);
                return;
            }

            // More than one item along is a jump, not a scroll, and is cut rather than travelled:
            // dragging the whole list past a window this size inside one transition reads as a blur
            // of everything in between rather than as a move to one thing. Cutting lands on the
            // destination and lets the colour crossfade carry the change instead.
            //
            // Only clicks can reach this now — an arrow step is adjacent by definition, and the
            // drift does not come through here to place the list at all.
            var jump = current >= 0 && Math.abs(index - current) > 1;

            current = index;
            items[current].classList.add('active');
            items[current].querySelector('[data-hero-select]').setAttribute('aria-pressed', 'true');
            var arrivingCta = items[current].querySelector('[data-hero-cta]');
            if (arrivingCta) arrivingCta.removeAttribute('tabindex');

            // Only when the visitor is the one who moved the list, and only from an input that
            // leaves focus where it was — the arrow keys, and a scroll in static mode. The drift
            // arriving at an item is not news worth interrupting anyone for: it would announce a
            // title every few seconds for as long as the page is open, unasked, on a loop that never
            // ends. A click needs no help either, since focus is on the item and a screen reader has
            // just read it.
            if (announce && liveRegion) {
                var titleEl = items[current].querySelector('[data-hero-title]');
                liveRegion.textContent = titleEl ? titleEl.textContent.trim() : '';
            }

            // The drift places the list itself, the wrap included; a jump only fades here when the
            // visitor made it.
            if (jump && !owned) {
                fadeJump(current);
            } else {
                cancelFadeJump();
                setPosition(current, animate !== false && !jump);
            }
        }

        // A jump cuts while the text is faded out, as the wrap does. The drift leaves the scroll
        // position alone until then, so the cut cannot show.
        var fadeOut = null;
        var fadeIn = null;
        var jumpPending = false;

        function cancelFadeJump() {
            jumpPending = false;
            if (fadeOut) fadeOut.cancel();
            if (fadeIn) fadeIn.cancel();
            fadeOut = fadeIn = null;
        }

        function fadeJump(index) {
            cancelFadeJump();
            if (!track.animate || reduceMotion.matches || nav.classList.contains('is-compact')) {
                setPosition(index, false);
                return;
            }
            var half = WRAP_FADE * 500;
            var out = fadeOut = track.animate(
                [{ opacity: 1 }, { opacity: 0 }],
                { duration: half, easing: 'ease-in', fill: 'forwards' }
            );
            jumpPending = true;
            out.finished.then(function () {
                if (fadeOut !== out) return;
                jumpPending = false;
                nav.scrollTo({ top: offsetFor(index), behavior: 'instant' });
                // Started before the fade-out is dropped, so no frame shows the text at full.
                fadeIn = track.animate([{ opacity: 0 }, { opacity: 1 }], { duration: half, easing: 'ease-out' });
                out.cancel();
                fadeOut = null;
            }).catch(function () { });
        }

        /**
         * Playback-driven sync, and the visitor's right to interrupt it.
         *
         * The list is a function of the video's clock rather than a series of events fired at it.
         * Every item owns an equal slice of the runtime and is centred at the start of its own
         * slice, so the list travels exactly one item's worth of the window per slice and the
         * position is defined for every instant in between. The last item's slice hands back to the
         * first with a fade rather than travelling; see wrapEased().
         *
         * Vimeo reports a position roughly every 266ms. Writing the list from those reports alone
         * would move it not quite four times a second, which is coarser than the transition it
         * replaces; so the reports set a clock and the frames in between extrapolate from it.
         */

        function sliceLength() {
            return duration > 0 ? duration / itemCount : 0;
        }

        function indexAt(seconds) {
            var slice = sliceLength();
            if (!slice) return 0;
            return Math.min(itemCount - 1, Math.max(0, Math.floor(seconds / slice)));
        }

        // 6p^5 - 15p^4 + 10p^3. Zero velocity and zero acceleration at both ends, which is the whole
        // reason for choosing it over a straight line: the list is at its slowest exactly where an
        // item is centred and quickest through the gap between two, where nothing is legible anyway.
        // On today's four items and 22s runtime a straight line would move the list at a flat 25px/s
        // and never let anything settle; this peaks near 47px/s in the middle of a handover and comes
        // to rest at each end, which is what keeps the copy readable and the CTA still while it is
        // the one being offered.
        function smootherstep(p) {
            if (p <= 0) return 0;
            if (p >= 1) return 1;
            return p * p * p * (p * (p * 6 - 15) + 10);
        }

        // Where the list belongs at `seconds`: which item is handing over, and how far through.
        function driftAt(seconds) {
            var slice = sliceLength();
            var index = indexAt(seconds);
            var p = slice ? (seconds - index * slice) / slice : 0;
            p = Math.min(1, Math.max(0, p));
            return { index: index, p: p, eased: isWrap(index) ? wrapEased(p, slice) : smootherstep(p) };
        }

        function isWrap(index) {
            return index === itemCount - 1;
        }

        // The last item's handover runs over WRAP_FADE centred on the slice's midpoint, where the lit
        // state changes hands anyway, rather than across the whole slice.
        function wrapEased(p, slice) {
            var half = slice ? Math.min(WRAP_FADE / 2, slice / 4) / slice : 0.25;
            return smootherstep((p - 0.5 + half) / (2 * half));
        }

        // The text fades out over the first half of the wrap and back in over the second, and the
        // list jumps to the first item while it is invisible.
        function offsetAt(drift) {
            var from = offsets[drift.index];
            if (from === undefined) return 0;
            if (isWrap(drift.index)) return drift.eased < 0.5 ? from : offsets[0];
            return from + (offsets[drift.index + 1] - from) * drift.eased;
        }

        var trackOpacity = null;
        function setTrackOpacity(value) {
            if (value === trackOpacity) return;
            trackOpacity = value;
            track.style.opacity = value === null ? '' : String(value);
        }

        // Only the two items involved in a handover are written, and only when the value actually
        // changes — the rest are left to the stylesheet. An inline custom property outranks the base
        // rule and .active exactly as an inline opacity did, so it has to be cleared again when the
        // list is handed back; see release().
        //
        // The item's place on the colour ramp is the whole of what the drift paints. Everything
        // inside it — the title, the body, the CTA — is on `color: inherit`, so one property moves
        // all of them and none of them can travel on a curve of its own.
        function setInk(key, el, value) {
            if (!el || paintedValues[key] === value) return;
            paintedValues[key] = value;
            if (value === null) el.style.removeProperty('--hero-t');
            else el.style.setProperty('--hero-t', String(value));
        }

        // The CTA travels on its item's curve rather than on the .active class.
        //
        // Left to the stylesheet it fades over its own fixed 0.3s the moment .active changes hands,
        // which on a 5.5s slice means it has been gone for seconds while the words it belongs to are
        // still on their way out — the CTA and its own text visibly leaving at different times. Same
        // curve, same clock, and the two now finish together.
        //
        // The item leaving a handover is at t = 1 - eased and the one arriving at t = eased, so the
        // pair crosses at the midpoint and the list always sums to one item's worth of ink.
        //
        // The CTA is deliberately not painted here. It used to be — first on its own opacity, then
        // on a product of that and its item's — and both were attempts to keep two separately
        // animated things in step. It inherits the item's colour instead, which is not a curve that
        // has to be kept in step with anything: it is the same one.
        function paintDrift(drift) {
            var leaving = drift.index;
            var arriving = (leaving + 1) % itemCount;
            for (var i = 0; i < slots.length; i++) {
                var t = i === leaving ? 1 - drift.eased : (i === arriving ? drift.eased : null);
                setInk('i' + i, slots[i], t);
            }
            var wrapping = isWrap(drift.index) && drift.eased > 0 && drift.eased < 1;
            setTrackOpacity(wrapping ? Math.abs(1 - 2 * drift.eased) : null);
        }

        function clearPaint() {
            for (var i = 0; i < slots.length; i++) {
                setInk('i' + i, slots[i], null);
            }
            setTrackOpacity(null);
        }

        function noteTime(seconds) {
            clockSeconds = seconds;
            clockAt = window.performance ? window.performance.now() : Date.now();
        }

        // The clock between reports. Extrapolation is capped at CLOCK_LEAD so a stall cannot run the
        // list past the picture, and held monotonic so the small corrections that arrive with every
        // report are absorbed rather than shown as a twitch backwards. A real jump back — the loop
        // coming round — is not a correction and is let through by onTimeUpdate resetting the guard.
        function estimatedSeconds() {
            if (!clockRunning) return clockSeconds;
            var now = window.performance ? window.performance.now() : Date.now();
            var estimate = clockSeconds + Math.min((now - clockAt) / 1000, CLOCK_LEAD);
            if (estimate < lastEstimate) estimate = lastEstimate;
            lastEstimate = estimate;
            return estimate;
        }

        /**
         * The drift loop.
         *
         * Ownership and running are two different things. `owned` says the drift is responsible for
         * the scroll position, and carries .is-drifting with it, which is what takes mandatory snap
         * and smooth scrolling out of the way — both of them round or queue the per-frame writes into
         * something worse than the stepping this replaces. Ownership survives a pause, so a paused
         * video leaves the list exactly where it was frozen; handing a mid-handover position back to
         * mandatory snap would jump it to the nearer item at the moment the visitor pressed pause.
         *
         * The loop itself only runs while there is something to animate.
         */

        // Two different questions, and conflating them costs a visible jump. Whether the drift may
        // own the list at all is about who the list belongs to; whether it can animate right now is
        // about whether there is any point. A hero scrolled out of view is the second, not the first:
        // giving the list up there would hand a mid-handover position back to mandatory snap and pull
        // it to the nearer item, so it would be waiting in the wrong place on the way back.
        function mustRelease() {
            return !player
                || !duration
                || itemCount < 2
                || detached
                || reduceMotion.matches
                || nav.classList.contains('is-compact')
                || nav.classList.contains('is-static');
        }

        function canRun() {
            return clockRunning && onScreen && !document.hidden;
        }

        function frame() {
            rafId = null;
            // Neither of these reschedules. Every path that changes the answer to canRun() calls
            // updateDrift(), which is what starts the loop again — so bailing here cannot strand it,
            // whereas rescheduling would leave it repainting a frozen position for as long as the
            // page is open.
            if (!owned || !canRun()) return;

            var drift = driftAt(estimatedSeconds());
            var target = offsetAt(drift);

            // The wrap jumps while the text is faded out; easing would scroll back through the list.
            if (isWrap(drift.index) && drift.eased > 0) catchUp = false;

            if (catchUp) {
                // Easing onto the drift rather than arriving at it, for the cases where the list is
                // somewhere else when playback takes it back: a resync after browsing, or a return
                // from a tab that stopped serving frames. Exponential, so it is quick where the gap
                // is wide and unhurried as it closes.
                var gap = target - nav.scrollTop;
                if (Math.abs(gap) < 1) {
                    catchUp = false;
                } else {
                    nav.scrollTop = nav.scrollTop + gap * CATCHUP_RATE;
                }
            }

            if (!catchUp && !jumpPending && Math.abs(nav.scrollTop - target) >= 0.5) {
                nav.scrollTop = target;
            }

            paintDrift(drift);

            // The item nearest the centre is the one that gets the lit state, the reachable CTA and
            // the aria-pressed — so it changes hands halfway through a handover, at the curve's
            // fastest and least legible point, rather than at either end where an item is settled
            // and a 0.3s fade on the CTA would be the only thing moving.
            var nearest = (drift.p >= 0.5 ? drift.index + 1 : drift.index) % itemCount;
            if (nearest !== current) setActive(nearest, false);

            schedule();
        }

        function schedule() {
            if (rafId === null) rafId = window.requestAnimationFrame(frame);
        }

        // Takes the scroll position. Easing onto it is for acquiring it — arriving from wherever the
        // list was left — and must not be re-armed on the way past: updateDrift() runs on every
        // report, so setting catchUp here unconditionally leaves the drift permanently easing toward
        // a moving target. That has a steady-state error rather than a converging one, at roughly
        // velocity / CATCHUP_RATE, which measured as a 3px lag that never closed — the last item
        // holding at 417 where its offset is 420, and the loop's cut sliding back through the list
        // over several frames instead of cutting. Callers that really are re-acquiring set catchUp
        // themselves.
        function claim() {
            if (!owned) {
                owned = true;
                nav.classList.add('is-drifting');
                catchUp = true;
            }
            schedule();
        }

        // Stops animating but keeps the position. For a pause, a hidden tab, a hero scrolled out of
        // view — anything that will hand back to the same drift where it left off.
        function suspend() {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
                rafId = null;
            }
            // Stopped mid-wrap, the text would stay faded out. Settle it on the item in front.
            if (trackOpacity !== null) {
                nav.scrollTo({ top: offsets[current] || 0, behavior: 'instant' });
                setTrackOpacity(null);
            }
        }

        // Gives the list up for good: to a gesture, to compact mode, to a video that never started,
        // to reduced motion. The inline paint has to go with it or the stylesheet's own states —
        // .active, the hover lift, the brighter neighbours under .is-detached — would all be
        // outranked by whatever the last frame happened to write.
        function release() {
            suspend();
            if (!owned) return;
            owned = false;
            nav.classList.remove('is-drifting');
            catchUp = false;
            clearPaint();
        }

        function updateDrift() {
            if (mustRelease()) {
                release();
                return;
            }
            // Paused, hidden or off screen: hold the position and the lighting exactly as they are.
            if (!canRun()) {
                suspend();
                return;
            }
            claim();
        }

        function onTimeUpdate(data) {
            // The runtime is what the whole mapping is built on and it only arrives with playback —
            // the first report carries it, about a quarter of a second in. Until then there is no
            // slice length, so there is nowhere to put the list and the run-in holds.
            if (data.duration && data.duration !== duration) {
                duration = data.duration;
            }

            // The loop coming round, which is a cut rather than a correction: the list has to go
            // from the last item back to the first in one step, so the monotonic guard is reset to
            // let time move backwards this once. The drift's own maths then places it on the first
            // item on the next frame. Vimeo's `ended` never fires here — the embed is built with
            // loop=1 by vimeoBackgroundEmbedSrc() — so a report going backwards is the only notice.
            if (data.seconds < clockSeconds - WRAP_BACK) {
                lastEstimate = 0;
                catchUp = false;
            }

            noteTime(data.seconds);
            clockRunning = true;
            liveIndex = indexAt(data.seconds);

            updateDrift();

            // Where the drift is not running — reduced motion, compact mode, a viewport that has
            // scrolled the hero away — the list still keeps step, one item per slice, changing at
            // the boundary. This is the behaviour the drift replaced, kept for the cases that
            // cannot have it.
            if (!owned && !detached) setActive(liveIndex, true);
        }

        // Whether playback is what moves the list.
        //
        // In static mode it is not: the video never started, so the window is handed back to the
        // browser's own scrolling — the only state where that happens now, and the only way the words
        // past the first can be reached when there is no clock to bring them forward. Two things have
        // to stand down there. Arrow stepping, because it would fight the scrolling the visitor is
        // doing; and re-syncing, which would be a promise the list cannot keep — liveIndex never
        // advances without playback, so the timer would fire and pull the list back to the run-in,
        // i.e. hide it, for a visitor who had just tabbed into it.
        //
        // Static mode is left behind the moment playback does start (see the play handler), which
        // is what keeps this from outliving the condition it describes.
        function isDriven() {
            return !!player && !nav.classList.contains('is-static');
        }

        // Gives the list back to playback, once the visitor is really done with it.
        function resync() {
            // Focus still inside the list is still interaction, whatever the clock says. Resyncing
            // out from under a keyboard visitor would move the item they are reading and, worse,
            // strip the tabindex from the CTA they are focused on and fade it to nothing — leaving
            // them on an invisible link that still answers Enter. Rearm and ask again later.
            if (nav.contains(document.activeElement)) {
                idleTimer = setTimeout(resync, RESYNC_DELAY);
                return;
            }

            detached = false;
            nav.classList.remove('is-detached');
            // Ownership first, so the drift is the thing that moves the list back — easing onto the
            // live position from wherever the visitor left it, rather than setActive() scrolling to
            // the nearest item only for the next frame to slide it off again.
            updateDrift();
            if (!owned) setActive(liveIndex, true);
        }

        // Hands the list to the visitor and starts the clock on giving it back. Called by every
        // gesture that browses the list, so the delay is idle time rather than a countdown from
        // the first touch.
        function detach() {
            if (!isDriven()) return;
            detached = true;
            nav.classList.add('is-detached');
            // Handed over completely: scroll-behavior comes back so setPosition() glides to the item
            // the visitor asked for, and the inline paint goes so .is-detached can brighten the
            // neighbours they are stepping through.
            release();
            clearTimeout(idleTimer);
            idleTimer = setTimeout(resync, RESYNC_DELAY);
        }

        // Where a step of `delta` would land, or null when there is nowhere to go. Split out from
        // step() so the key handler can ask the question without moving anything — it has to know
        // whether the list could still take the key before deciding to swallow it.
        //
        // Stops at both ends rather than wrapping. That now disagrees with the drift, which does
        // wrap: playback carries on from the last item into the first, while an arrow key stops
        // dead there. Left as it stands deliberately rather than by oversight — the two ends are a
        // useful edge to feel when stepping by hand — but it is the inconsistency to revisit first
        // if keyboard browsing should cycle too.
        //
        // The run-in has no item of its own to step from, so stepping forward out of it starts at the
        // first item and stepping back starts at the last.
        function stepTarget(delta) {
            var from = current < 0 ? (delta > 0 ? -1 : itemCount) : current;
            var next = from + delta;

            if (next < 0 || next > itemCount - 1) return null;

            return next === current ? null : next;
        }

        // Returns whether it actually moved, which is what lets the key handler decide between
        // consuming the key and letting the page have it.
        function step(delta) {
            var next = stepTarget(delta);
            if (next === null) return false;

            detach();
            // Announced: this is the one way the list moves that leaves focus untouched, so a
            // screen reader would otherwise have no way of knowing anything had changed.
            setActive(next, true, true);
            return true;
        }

        track.addEventListener('click', function (e) {
            var select = e.target.closest('[data-hero-select]');
            if (!select) return;
            var item = select.closest('[data-hero-item]');
            var index = parseInt(item.getAttribute('data-index'), 10);
            if (isNaN(index)) return;
            detach();
            setActive(index, true);
        });

        nav.addEventListener('keydown', function (e) {
            // Arrow keys move item to item, which is more use here than the free scrolling the
            // browser would otherwise do with them, and they are only swallowed when the list took
            // them: at either end the key belongs to the page, because the list has nowhere left to
            // go and no longer pretends otherwise by wrapping round.
            if (!isDriven()) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                if (step(1)) e.preventDefault();
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                if (step(-1)) e.preventDefault();
            }
        });

        // Which item the list came to rest on, once it has — and now only ever in static mode, the
        // one state that still lets a gesture move it. Everywhere else the list is not the visitor's
        // to scroll, so there is nothing to read back: the drift's own position is already in step
        // with `current`, and an arrow step or a click set `current` before moving anything, which
        // makes reading it off the scroll position a round trip to learn what was just decided.
        //
        // Debounced rather than taken per event, so a flick's momentum tail is read once where it
        // stops rather than at every frame along the way.
        var scrollSettleTimer;
        nav.addEventListener('scroll', function () {
            if (isDriven()) return;
            clearTimeout(scrollSettleTimer);
            scrollSettleTimer = setTimeout(function () {
                var centre = nav.scrollTop + nav.clientHeight / 2;
                var nearest = 0;
                var shortest = Infinity;
                for (var i = 0; i < itemCount; i++) {
                    var distance = Math.abs(items[i].offsetTop + items[i].offsetHeight / 2 - centre);
                    if (distance < shortest) {
                        shortest = distance;
                        nearest = i;
                    }
                }
                // animate: false because the list is already there — this is recording where the
                // visitor put it, not moving it. Announced for the same reason an arrow step is:
                // the item in front changed without focus going anywhere.
                if (nearest !== current) setActive(nearest, false, true);
            }, SCROLL_SETTLE_DELAY);
        }, { passive: true });

        // Focus arriving from the keyboard counts as browsing: without this, tabbing to an
        // item's CTA would be undone by the drift moving the list out from under it.
        nav.addEventListener('focusin', function () {
            // The run-in hides the list but deliberately leaves it focusable, so a keyboard
            // visitor can arrive here during the opening stretch with nothing on screen. Bring the
            // first item forward for them, which is what an arrow step out of the run-in does
            // anyway — and unlike that, focus alone would otherwise never clear it.
            if (current < 0) setActive(0, false);
            detach();
        });

        /**
         * The video itself.
         */

        function applyMuted() {
            if (!player) return;
            player.setMuted(isMuted).catch(function () { });
            if (!muteBtn) return;
            muteBtn.classList.toggle('is-unmuted', !isMuted);
            muteBtn.setAttribute('aria-label', isMuted ? 'Unmute' : 'Mute');
        }

        // Once only, and on the first `play` rather than on ready(): the label reads as a caption
        // to a video that is running, so it has nothing to say while the poster is still the whole
        // of the hero, and re-showing it every time playback resumes would make it an alert about
        // the pause button instead. Its own timer takes it away; a click on either sound control
        // takes it away sooner.
        function showSoundLabel() {
            if (!muteBtn || soundLabelShown) return;
            soundLabelShown = true;
            muteBtn.classList.add('is-prompting');
            setTimeout(hideSoundLabel, SOUND_LABEL_DELAY);
        }

        // Fades out rather than disappearing — the stylesheet transitions visibility alongside the
        // opacity, so the words stop being drawn at the end of the fade rather than at its start.
        function hideSoundLabel() {
            if (muteBtn) muteBtn.classList.remove('is-prompting');
        }

        function markPaused(paused) {
            if (!playBtn) return;
            playBtn.classList.toggle('is-paused', paused);
            playBtn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
        }

        // Nothing is going to drive the list, so hand the window to native scrolling and put the
        // first item in front. Reached when autoplay is refused, when the embed fails, and when
        // the player simply never answers — the list is rendered hidden, so every one of those
        // has to end somewhere the text is on screen rather than in a run-in that never lifts.
        function fallBackToStatic() {
            if (nav.classList.contains('is-static')) return;
            nav.classList.add('is-static');
            release();
            setActive(0, false);
            markPaused(true);
            // Nothing here touches the sound label: it only ever appears once playback has
            // actually started, and refused autoplay is precisely the case where it has not. The
            // play button carries the invitation instead, and the click it wants is the gesture
            // the browser was holding out for.
        }

        function startVideo() {
            if (!iframe || player) return;

            iframe.src = iframe.getAttribute('data-src');

            try {
                player = new VimeoPlayer(iframe);
            } catch (e) {
                // @vimeo/player checks the embed synchronously and throws outright on anything
                // it does not recognise ("The player element passed isn't a Vimeo embed").
                // vimeoBackgroundEmbedSrc() hands back whatever it was given when it cannot find
                // an id in it, so a YouTube link or a typo in the CMS's video_link reaches this
                // line intact. Letting the throw escape would abandon the rest of setup with the
                // list still hidden, costing the hero its words over one bad field.
                player = null;
                fallBackToStatic();
                return;
            }

            // ready() rejects on a load error but not on a third party that simply never
            // replies — a blocked embed, a dead network — and that silence would otherwise
            // leave the hero showing a poster and no words at all.
            var readyFallback = setTimeout(fallBackToStatic, READY_TIMEOUT);

            player.on('timeupdate', onTimeUpdate);
            player.on('play', function () {
                markPaused(false);
                // Playback is the whole condition for driving the list, so this is where static
                // mode has to end — not just where ready() resolved. A refused autoplay drops to
                // static and can be started by hand afterwards, and without this the clock would
                // then drive the list against a pinned track: the highlight walking items that
                // never scroll into view, and the wrap hiding the list outright every loop.
                nav.classList.remove('is-static');
                // Re-stamped, not just re-enabled. clockAt is from before the pause, so without this
                // the first frames back extrapolate from a wall clock that has moved on without the
                // video — capped by CLOCK_LEAD, but still half a second ahead of the picture.
                noteTime(clockSeconds);
                lastEstimate = 0;
                clockRunning = true;
                updateDrift();
                // Fades the embed up over the poster. Only on play, so the still frame covers
                // Vimeo's own black box for as long as the player takes to start.
                hero.classList.add('is-playing');
                showSoundLabel();
            });
            player.on('pause', function () {
                markPaused(true);
                // The clock stops, so the drift stops with it — and because the list is still owned,
                // it holds the position and the lighting it was frozen at rather than snapping to
                // the nearer item. This is also what satisfies WCAG 2.2.2: the pause button really
                // does stop the moving content, because the movement is the clock.
                clockRunning = false;
                updateDrift();
            });

            // A stalled embed keeps reporting nothing while the picture sits still, and an
            // extrapolating clock would walk the list on without it. CLOCK_LEAD caps how far that can
            // go on its own; these stop it at the source.
            player.on('bufferstart', function () {
                clockRunning = false;
                updateDrift();
            });
            player.on('bufferend', function () {
                clockRunning = true;
                lastEstimate = 0;
                updateDrift();
            });

            // Nothing in the hero seeks by hand — a background embed has no controls of its own —
            // but the loop is a seek, and it announces itself twice: a timeupdate reporting 0, which
            // onTimeUpdate reads as the wrap and cuts on, and then a `seeked` at about 0.01s. Taking
            // that second one at face value re-armed the ease immediately after the cut had been
            // decided, and the list slid back up through every item at CATCHUP_RATE instead of
            // cutting — 420px unwound over a dozen frames, which is the exact traverse the cut
            // exists to avoid.
            //
            // So a seek to where the list already is changes nothing. Only a real move eases, and
            // only a forward one: anything backwards is cut for the same reason the wrap is.
            player.on('seeked', function (data) {
                var delta = data.seconds - clockSeconds;
                lastEstimate = 0;
                noteTime(data.seconds);
                if (Math.abs(delta) > WRAP_BACK) catchUp = delta > 0;
                updateDrift();
            });

            player.ready().then(function () {
                clearTimeout(readyFallback);
                nav.classList.remove('is-static');
                setActive(-1, false);
                if (controls) controls.hidden = false;
                applyMuted();
                // background=1 already asks for autoplay; this covers the embed becoming ready
                // after the browser's decision, and is a no-op when it is already running.
                return player.play();
            }).catch(function () {
                // Autoplay refused, or the embed failed to load. The poster is already on
                // screen, so leave the list somewhere a visitor can use it and offer the
                // controls rather than waiting on a clock that will never start.
                clearTimeout(readyFallback);
                fallBackToStatic();
            });
        }

        if (muteBtn) {
            muteBtn.addEventListener('click', function () {
                isMuted = !isMuted;
                applyMuted();
                // Taken, so the invitation has served its purpose — and marked as shown, so a
                // later pause and resume cannot bring it back after it has been acted on.
                soundLabelShown = true;
                hideSoundLabel();
                // A click is the gesture browsers want before audio, so this is also the moment a
                // refused autoplay is most likely to be allowed to start.
                if (player && !isMuted) player.play().catch(function () { });
            });
        }

        // The dim layer. Deliberately not remembered between loads: this hero starts undimmed —
        // the class and the matching aria-label ship in video-hero.tpl.php — so every arrival
        // comes back to the bare frame and dimming is a momentary choice about the view in front
        // of you. Nothing here touches the player: the layer sits over the poster just as much as
        // over the embed, which is why the button lives outside .video-hero-controls and works
        // before playback has started or when it never does.
        if (dimBtn) {
            dimBtn.addEventListener('click', function () {
                var undimmed = hero.classList.toggle('is-undimmed');
                // Kept in step with the tooltip the same class switches, which is this button's
                // visible label; the two have to read alike for voice control.
                dimBtn.setAttribute('aria-label', undimmed ? 'Dim video' : 'Brighten video');
            });
        }

        if (playBtn) {
            playBtn.addEventListener('click', function () {
                // Only reachable if the embed was rejected outright, which leaves the controls
                // hidden — so this is a guard against a null player rather than a route anyone
                // takes. startVideo() no-ops on a second call unless that is what happened.
                if (!player) {
                    startVideo();
                    return;
                }
                if (playBtn.classList.contains('is-paused')) {
                    player.play().catch(function () { });
                } else {
                    player.pause().catch(function () { });
                }
            });
        }

        /**
         * Compact mode — a viewport too short even for the one-item window shows the item in front
         * on its own, with nothing to scroll. Ported from crossfade-carousel.js.
         */

        var resizeTimer;
        function checkCompactMode() {
            var geometry = measureGeometry();

            var siteHeader = document.querySelector('.site-header');
            var heroRect = hero.getBoundingClientRect();
            var topClearance = siteHeader
                ? Math.max(0, siteHeader.getBoundingClientRect().bottom - heroRect.top)
                : 0;
            var navBottom = parseFloat(window.getComputedStyle(nav).bottom) || 0;
            var available = hero.offsetHeight - topClearance - navBottom;

            if (geometry.height > available) {
                nav.classList.add('is-compact');
                nav.style.height = '';
                // Cleared with the window: only the item in front is displayed in this mode, so
                // padding sized to centre it would be scrollable space around nothing, and a height
                // equalised to the tallest item would pad a short one out for no one's benefit.
                track.style.paddingTop = '';
                track.style.paddingBottom = '';
                clearItemHeights();
                // Nothing to scroll and nothing to interpolate between: only the item in front is
                // displayed. The drift gives the list up and the boundary change in onTimeUpdate
                // carries it instead.
                updateDrift();
                setActive(liveIndex >= 0 ? liveIndex : current, false);
                return;
            }

            // Applied on every pass rather than only on the way out of compact mode. The mode
            // staying the same does not mean the measurements have: a width change rewraps the copy
            // and moves them, so an early return on the mode alone would leave the window and its
            // padding sized for the viewport before last.
            nav.classList.remove('is-compact');
            applyGeometry();
            setPosition(current, false);
            // applyGeometry() has just moved every offset the drift interpolates between, so the
            // position it is holding belongs to the layout before this one. Easing onto the new
            // geometry rather than letting the next frame jump there.
            catchUp = true;
            updateDrift();
        }

        checkCompactMode();
        window.addEventListener('resize', function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(checkCompactMode, 150);
        });

        // Reduced motion is a preference, not a build-time constant: honouring a change to it while
        // the page is open is the difference between the setting working and the setting working
        // after a reload. The list keeps step either way — it just stops travelling between items.
        if (reduceMotion.addEventListener) {
            reduceMotion.addEventListener('change', updateDrift);
        } else if (reduceMotion.addListener) {
            reduceMotion.addListener(updateDrift);
        }

        // A hero scrolled off the top of the page is still playing, and driving a scroll container
        // nobody is looking at costs a write, a layout and a scroll event on every frame. The
        // position is picked up again on the way back, eased rather than jumped.
        if (window.IntersectionObserver) {
            new window.IntersectionObserver(function (entries) {
                onScreen = entries[entries.length - 1].isIntersecting;
                updateDrift();
            }).observe(hero);
        }

        // requestAnimationFrame stops being served to a hidden tab, so the loop pauses itself and the
        // clock's own CLOCK_LEAD cap stops the estimate running away in the meantime. All this adds
        // is the ease back on, rather than resuming with a jump to wherever the video got to.
        document.addEventListener('visibilitychange', function () {
            if (!document.hidden) {
                lastEstimate = 0;
                catchUp = true;
            }
            updateDrift();
        });

        // Started for everyone, reduced motion included: the video is the hero, and the pause
        // button is right there for anyone who wants it stopped. What reduced motion switches off is
        // the list's own movement — the drift never claims the list (see mustRelease) and the
        // stylesheet drops the ramp transitions — so it still keeps step with the runtime, one item per
        // slice, it just cuts rather than travels.
        //
        // The age gate is the one thing it waits on, and only where one is actually going to
        // open; see whenAgeGateCleared() above.
        whenAgeGateCleared(startVideo);
    });
}();
